import * as React from "react";
import Divider from "@mui/material/Divider";
import Drawer, { DrawerProps } from "@mui/material/Drawer";
import List from "@mui/material/List";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LogoutIcon from "@mui/icons-material/Logout";
import { CardMedia, createSvgIcon, MenuItem } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import MapIcon from "@mui/icons-material/Map";
import BadgeIcon from "@mui/icons-material/Badge";

import { useAppDispatch } from "../../hooks";
import { menuSelected } from "./navigatorSlice";
import { black, gold, white } from "../../getLPTheme";

const SlackColor = createSvgIcon(
  <svg
    width="800px"
    height="800px"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M26.5002 14.9996C27.8808 14.9996 29 13.8804 29 12.4998C29 11.1192 27.8807 10 26.5001 10C25.1194 10 24 11.1193 24 12.5V14.9996H26.5002ZM19.5 14.9996C20.8807 14.9996 22 13.8803 22 12.4996V5.5C22 4.11929 20.8807 3 19.5 3C18.1193 3 17 4.11929 17 5.5V12.4996C17 13.8803 18.1193 14.9996 19.5 14.9996Z"
      fill="#2EB67D"
    />
    <path
      d="M5.49979 17.0004C4.11919 17.0004 3 18.1196 3 19.5002C3 20.8808 4.1193 22 5.49989 22C6.8806 22 8 20.8807 8 19.5V17.0004H5.49979ZM12.5 17.0004C11.1193 17.0004 10 18.1197 10 19.5004V26.5C10 27.8807 11.1193 29 12.5 29C13.8807 29 15 27.8807 15 26.5V19.5004C15 18.1197 13.8807 17.0004 12.5 17.0004Z"
      fill="#E01E5A"
    />
    <path
      d="M17.0004 26.5002C17.0004 27.8808 18.1196 29 19.5002 29C20.8808 29 22 27.8807 22 26.5001C22 25.1194 20.8807 24 19.5 24L17.0004 24L17.0004 26.5002ZM17.0004 19.5C17.0004 20.8807 18.1197 22 19.5004 22L26.5 22C27.8807 22 29 20.8807 29 19.5C29 18.1193 27.8807 17 26.5 17L19.5004 17C18.1197 17 17.0004 18.1193 17.0004 19.5Z"
      fill="#ECB22E"
    />
    <path
      d="M14.9996 5.49979C14.9996 4.11919 13.8804 3 12.4998 3C11.1192 3 10 4.1193 10 5.49989C10 6.88061 11.1193 8 12.5 8L14.9996 8L14.9996 5.49979ZM14.9996 12.5C14.9996 11.1193 13.8803 10 12.4996 10L5.5 10C4.11929 10 3 11.1193 3 12.5C3 13.8807 4.11929 15 5.5 15L12.4996 15C13.8803 15 14.9996 13.8807 14.9996 12.5Z"
      fill="#36C5F0"
    />
  </svg>,
  "Slack"
);

const categories = [
  { id: "Notificaciones", icon: <NotificationsActiveIcon />, active: false },
  { id: "Editar Perfil", icon: <SettingsIcon /> },
  { id: "Suscripción", icon: <ReceiptLongIcon /> },
  { id: "Mapa", icon: <MapIcon /> },
  { id: "Mi Credencial", icon: <BadgeIcon /> },
];

const item = {
  py: "2px",
  px: 3,
  color: white[50],
  "&:hover, &:focus": {
    bgcolor: gold[500],
    color: black[1000],
  },
};

const itemCategory = {
  boxShadow: "0 -1px 0 black[1000] inset",
  py: 1.5,
  px: 3,
};

type NavigatorProps = DrawerProps & {
  /**
   * Callback opcional para cerrar el drawer en mobile.
   * El padre lo pasa como: () => setMobileOpen(false)
   */
  onNavigate?: () => void;
};

export default function Navigator(props: NavigatorProps) {
  const { onNavigate, ...other } = props;

  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const closeIfMobile = React.useCallback(() => {
    if (isMobile) onNavigate?.();
  }, [isMobile, onNavigate]);

  const handleLogout = async () => {
    localStorage.removeItem("CLUB-VIP-JWT");
    localStorage.removeItem("CLUB-VIP-AVATAR");
    localStorage.removeItem("CLUB-VIP-USER");
    closeIfMobile();
  };

  return (
    <Drawer variant="permanent" {...other}>
      <List disablePadding>
        <ListItem
          sx={{
            py: "2px",
            px: 3,
            ...itemCategory,
            fontSize: 15,
            color: black[1000],
          }}
        >
          <Box sx={{ px: 0 }}>
            <MenuItem>
              <Link to={"/"} style={{ textDecoration: "none" }} onClick={closeIfMobile}>
                <CardMedia
                  component="img"
                  image={"images/logoWorldGolfClubWhite.png"}
                  sx={{ width: 150, height: 50 }}
                />
              </Link>
            </MenuItem>
          </Box>
        </ListItem>

        <ListItem>
          <ListItemIcon sx={{ color: white[50] }}>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText sx={{ color: white[50] }}>HOME</ListItemText>
        </ListItem>

        <Box key="User" sx={{ bgcolor: black[1000] }}>
          <ListItem sx={{ py: 2, px: 3 }}>
            <ListItemText sx={{ color: white[50] }}>Menu</ListItemText>
          </ListItem>

          <ListItem disablePadding key="User">
            <Box sx={{ py: "2px", px: 3, color: gold[500] }}>
              <ListItemIcon>
                <PeopleIcon sx={{ mx: 1 }} />
                User
              </ListItemIcon>
            </Box>
          </ListItem>

          <Divider sx={{ mt: 2 }} />
        </Box>

        <Box sx={{ bgcolor: black[1000] }}>
          {categories.map(({ id: childId, icon, active }) => (
            <ListItem disablePadding key={childId}>
              <ListItemButton
                selected={active}
                sx={item}
                onClick={() => {
                  dispatch(menuSelected(childId));
                  closeIfMobile();
                }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText>{childId}</ListItemText>
              </ListItemButton>
            </ListItem>
          ))}

          <Link
            to={"https://slack.com/intl/es-es"}
            target="_blank"
            style={{ textDecoration: "none" }}
            onClick={closeIfMobile}
          >
            <ListItem disablePadding>
              <ListItemButton sx={item}>
                <ListItemIcon>
                  <SlackColor />
                </ListItemIcon>
                <ListItemText>Slack</ListItemText>
              </ListItemButton>
            </ListItem>
          </Link>

          <Link to={"/login"} style={{ textDecoration: "none" }} onClick={handleLogout}>
            <ListItem disablePadding>
              <ListItemButton sx={item}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText>Cerrar Sesión</ListItemText>
              </ListItemButton>
            </ListItem>
          </Link>

          <Divider sx={{ mt: 2 }} />
        </Box>
      </List>
    </Drawer>
  );
}
